import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import {
  Button,
  Grid,
  withStyles,
  Link,
} from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useIntl } from 'react-intl';
import {
  cloneDeep,
  difference,
  findIndex,
  each,
  keys,
  map,
  startCase,
  lowerCase,
} from 'lodash';
import clsx from 'clsx';

import {
  FilterByName,
  FilterByKeyStoreName,
  FilterByDeploymentType,
  FilterAndSortSeparator,
} from './controls';
import styles from './styles';
import {
  FormActionButtons,
  Switch,
} from '../../../../components';
import ListContainer from '../../../list';
import {
  GRID_ROWS_PER_PAGE_EXTRA_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION_24,
 } from '../../../../constants';
import {
  fetchProxyList,
  updateKeyStoreProxy,
} from '../../../../actions/keyStore';
import {
  getIsProxyUpdateSuccess,
  getProxyListTotalPages,
  getProxyListTotalElements,
  getProxyListResults,
  getErrors,
} from '../../../../reducers/keyStore';
import { getI18n } from '../../../../utils/intl';

const CHANGE_MODE_REMOVE_ACCESS = 'CHANGE_MODE_REMOVE_ACCESS';
const CHANGE_MODE_ADD_ACCESS = 'CHANGE_MODE_ADD_ACCESS';

export const Proxies = (props) => {
  const {
    uuid,
    classes,
    isProxyUpdateSuccess,
    notifyMessages,
    rows = [],
    totalElements,
    totalPages,
    updateKeyStoreProxy,
    isCurrentTab,
  } = props;

  const { getI18nMessage } = getI18n(useIntl());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION_24);
  const [filterByName, setFilterByName] = useState('');
  const [filterByKeyStoreName, setFilterByKeyStoreName] = useState('');
  const [filterByDeploymentType, setFilterByDeploymentType] = useState('all');
  const [showFormActionBtns, setShowFormActionBtns] = useState(false);
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [currentProxyRows, setCurrentProxyRows] = useState([]);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const emptyFunction = () => {};

  useEffect(() => {
    if (uuid && isCurrentTab) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      fetchListWithFilters();
    }
  }, [uuid, isCurrentTab]);

  const getListColumns = ({ getI18nMessage, changeMode }) => [{
    id: 'proxyName',
    label: getI18nMessage('label.key.store.proxy.name'),
    minWidth: 200,
    value: (item) => (
      <Link underline="none" href={`/publish/api-proxy/details/${item.proxyUuid}`}>
        {item.proxyName}
      </Link>
    ),
  }, {
    id: 'proxyStatus',
    label: getI18nMessage('label.key.store.proxy.status'),
    minWidth: 100,
    value: (item) => formatAppStatus (item.proxyStatus),
  }, {
    id: 'keyStoreName',
    label: getI18nMessage('label.key.store.proxy.key.store.name'),
    minWidth: 100,
    value: (item) => item.keyStoreName,
    isHidden: changeMode !== CHANGE_MODE_ADD_ACCESS,
  }, {
    id: 'keyStoreDeploymentType',
    label: getI18nMessage('label.key.store.proxy.key.store.deployment.type'),
    minWidth: 100,
    value: (item) => startCase(lowerCase(item.keyStoreDeploymentType)),
    isHidden: changeMode !== CHANGE_MODE_ADD_ACCESS,
  }, {
    id: '',
    label: getI18nMessage('label.assign'),
    minWidth: 50,
    format: ({ hasAccess, uuid }) =>
      (<Switch
        onChange={(val) => onAccessToggle(val, uuid)}
        checked={hasAccess}
      />
    ),
    value: ({ proxyUuid, proxyKeyStoreAccessStatus }) => ({ uuid: proxyUuid, hasAccess: proxyKeyStoreAccessStatus === 'ASSIGNED' }),
    isHidden: changeMode === CHANGE_MODE_REMOVE_ACCESS,
  }];

  const formatAppStatus = (status) => {
    const statusClass = status === 'CONNECTED' ? classes.success : classes.error;
    return (
      <span className={classes.statusField}>
        <FiberManualRecordIcon
          className={clsx(classes.statusIcon, statusClass)}/>
        <span>{startCase(lowerCase(status))}</span>
      </span>);
  }

  useEffect(() => {
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) || isFormDirty) {
      setShowFormActionBtns(true);
    }
    if ((changeMode !== CHANGE_MODE_ADD_ACCESS) && !isFormDirty) {
      setShowFormActionBtns(false);
    }
  }, [changeMode, isFormDirty]);

  useEffect(() => {
    setCurrentProxyRows(cloneDeep(rows));
  }, [rows]);

  useEffect(() => {
    if (isProxyUpdateSuccess) {
      notifyMessages('success', getI18nMessage('label.key.store.updated'));
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      setPage(0);
      fetchListWithFilters();
      props.fetchKeyStore(uuid);
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    }
  }, [isProxyUpdateSuccess]);

  const onAccessToggle = async(val, uuid) => {
    updateCurrentProxyRow(uuid, !!val);
  };

  useEffect(() => {
   const rowsMap = {};
    each(rows, item => {
      rowsMap[item.proxyUuid] = item;
    });
    const currentProxyRowsMap = {};
    each(currentProxyRows, item => {
      currentProxyRowsMap[item.proxyUuid] = item;
    });
    const rowIds = keys(rowsMap);
    const currentProxyRowsIds = keys(currentProxyRowsMap);
    if ( difference(rowIds, currentProxyRowsIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(rowIds, id => {
      if (rowsMap[id].proxyKeyStoreAccessStatus !==
        currentProxyRowsMap[id].proxyKeyStoreAccessStatus) {
        modifiedRecords++;
      }
    });
    setModifiedRecords(modifiedRecords);
  }, [currentProxyRows]);

  useEffect(() => {
    if (modifiedRecords > 0) {
      setIsFormDirty(true);
      setModifiedRecords(modifiedRecords);
    } else {
      setIsFormDirty(false);
    }
  }, [modifiedRecords]);

  useEffect(() => {
    if (uuid) {
      fetchListWithFilters();
    }
  }, [changeMode]);

  const updateCurrentProxyRow = (uuid, access) => {
    const index = findIndex(currentProxyRows, item => item.proxyUuid === uuid);
    currentProxyRows[index].proxyKeyStoreAccessStatus = access ? 'ASSIGNED' : 'NOT_ASSIGNED';
    setCurrentProxyRows([...currentProxyRows]);
  };

  const fetchListWithFilters = ({
    hasAccess = changeMode === CHANGE_MODE_REMOVE_ACCESS ? true : false,
    name = filterByName,
    keyStoreName = filterByKeyStoreName,
    deploymentType = filterByDeploymentType,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
  } = { }) => {
    props.fetchProxyList({
      uuid,
      filterByName : name,
      keyStoreName,
      deploymentType,
      hasAccess,
      page: pageNum,
      size: rowsPerPageArg,
    });
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };

  const onKeyStoreNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };

  const onFilterByTypeChange = (type) => {
    setFilterByDeploymentType(type);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, deploymentType: type });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, rowsPerPageArg : newRowsPerPage });
  };

  const addButtonLabel = getI18nMessage('label.key.store.proxy.add');
  const handleAddButton = () => {
    setChangeMode(CHANGE_MODE_ADD_ACCESS);
    setPage(0);
  };
  const showAddBtn = changeMode === CHANGE_MODE_REMOVE_ACCESS;
  const addBtnFunc = showAddBtn ? handleAddButton : emptyFunction;

  const saveUpdatedRows = () => {
    if (changeMode === CHANGE_MODE_REMOVE_ACCESS) {
      const removedProxy = currentProxyRows.filter(item => item.proxyKeyStoreAccessStatus === 'NOT_ASSIGNED');
      if(removedProxy.length > 0 ) {
        const data = map(removedProxy,
            item => ({ uuid: item.proxyUuid }),
          );
          updateKeyStoreProxy({
          uuid,
          action: 'remove',
          data,
        });
      }
    }
  
    const addedProxy = currentProxyRows.filter(item => item.proxyKeyStoreAccessStatus === 'ASSIGNED');
    if (addedProxy.length > 0 && changeMode === CHANGE_MODE_ADD_ACCESS) {
      const data = map(addedProxy,
          item => ({ uuid: item.proxyUuid }),
        );
        updateKeyStoreProxy({
        uuid,
        data,
      });
    }
  };

  const onCancel = () => {
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) && !isFormDirty) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
    } else {
      setCurrentProxyRows(cloneDeep(rows));
    }
  }

  const assistText = isFormDirty ? (<span>
    {getI18nMessage('label.key.store.proxy.list.save.assist.text', { num: modifiedRecords })}
    <WarningIcon className={classes.warningIcon} />
    {getI18nMessage('label.redepolyment.required')}
  </span>) : '';

  return (
    <Fragment>
      <ListContainer
        hasPaginationDisabled={isFormDirty}
        listPageId="key-store-proxy-list"
        isLoading={false}
        notificationId="key-store-proxy-notifications"
        notificationStatus={''}
        setNotificationStatus={() => {}}
        notificationMessage={''}
        setNotificationMessage={() => {}}
        pageHeaderTitle={''}
        pageHeaderDescription={''}
        pageHeaderTooltipTitle={getI18nMessage('label.request.list.page.title.help')}
        addButtonLabel={''}
        onAdd={false}
        showListHeader={true}
        filterAndSortContent={(
          <Fragment>
            <FilterByName
              fieldContainerClass={classes.fieldContainer}
              handleChange={setFilterByName}
              name={getI18nMessage('label.filter')}
              onKeyPress={onFilterByNameKeyPress}
              placeholder={getI18nMessage('label.key.store.proxy.name')}
              value={filterByName}
            />
            {changeMode === CHANGE_MODE_ADD_ACCESS &&
              <>
                <FilterByKeyStoreName
                  fieldContainerClass={classes.fieldContainer}
                  handleChange={setFilterByKeyStoreName}
                  name={getI18nMessage('label.filter')}
                  onKeyPress={onKeyStoreNameKeyPress}
                  placeholder={getI18nMessage('label.key.store.proxy.key.store.name')}
                  value={filterByKeyStoreName}
                  hideLabel
                />
                <FilterByDeploymentType
                  fieldContainerClass={classes.fieldContainer}
                  name={getI18nMessage('label.filter.by')}
                  value={filterByDeploymentType}
                  handleChange={onFilterByTypeChange}
                  hideLabel
                />
                <FilterAndSortSeparator />
              </>
            }
            <Grid item md={4} sm={4} xs={6}>
              {showAddBtn && <Button
                className={classes.addBtn}
                data-apim-test={'key-store-proxy-add'}
                data-layer7-test={'key-store-proxy-add'}
                disabled={isFormDirty}
                id={'key-store-proxy-add'}
                onClick={addBtnFunc}
                variant="contained" color="secondary"
                fieldContainerClass={classes.fieldContainer}
              >
                {addButtonLabel}
              </Button>}
            </Grid>
          </Fragment>
        )}
        columns={getListColumns({ getI18nMessage, classes, changeMode })}
        rows={currentProxyRows}
        noResultsMessage={getI18nMessage('label.no.results')}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_EXTRA_OPTIONS}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        notifyMessages={emptyFunction}
        pageBodyClass={classes.pageBodyClass}
        pageClass={classes.pageClass}
      />
      {showFormActionBtns && <FormActionButtons
        assistText={assistText}
        isDisabled={!isFormDirty}
        nextText={'Save'}
        onCancelClick={onCancel}
        onNextClick={saveUpdatedRows}
        id="key-store-proxy-form-buttons"
      />}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  rows: getProxyListResults(state),
  totalElements: getProxyListTotalElements(state),
  totalPages: getProxyListTotalPages(state),
  isProxyUpdateSuccess: getIsProxyUpdateSuccess(state),
  errors: getErrors(state),
});

Proxies.propTypes = {
  uuid: string,
  classes: shape({}),
  fetchProxyList: func,
  notifyMessages: func,
  rows: arrayOf(shape({})),
  totalElements: number,
  totalPages: number,
  updateKeyStoreProxy: func,
  userContext: object,
  isProxyUpdateSuccess: bool,
  errors: arrayOf(shape({})),
  isCurrentTab: bool,
  fetchKeyStore: func,
};
Proxies.displayName = 'Proxies';

const mapDispatchToProps = {
  fetchProxyList,
  updateKeyStoreProxy,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Proxies);
